import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { knowledgeBaseApi, type KbCategory } from "@/api/knowledgeBase";

// Knowledge Base v1.0 PR 4 — Categories admin. Mirrors the user-facing
// AdminCategoriesPage shape but writes to the Knowledge Base namespace.
// System-reserved rows are read-only (delete + code-edit suppressed).

const QUERY_KEY = ["kb", "admin", "categories"] as const;

export function KnowledgeBaseCategoriesAdminPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");
  const toast = useToast();
  const qc = useQueryClient();
  const [editTarget, setEditTarget] = useState<KbCategory | "new" | null>(null);

  const listQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => knowledgeBaseApi.listCategories(),
    enabled: canManage,
  });

  const deleteMutation = useMutation({
    mutationFn: (code: string) => knowledgeBaseApi.deleteCategory(code),
    onSuccess: () => {
      toast.success("Category deleted.");
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) =>
      toast.error(
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Delete failed.",
      ),
  });

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage knowledge base categories.
      </div>
    );
  }

  const columns: Column<KbCategory>[] = [
    {
      key: "code",
      header: "Code",
      render: (c) => <span className="font-mono text-onyx font-semibold">{c.code}</span>,
    },
    {
      key: "name",
      header: "Name",
      render: (c) => (
        <span className="font-semibold text-onyx">
          {c.name}
          {c.isSystemReserved && (
            <span className="ml-space-2 text-caption text-gold-shadow uppercase tracking-wide">
              System
            </span>
          )}
        </span>
      ),
    },
    {
      key: "order",
      header: "Order",
      render: (c) => <span className="font-mono text-caption">{c.displayOrder}</span>,
    },
    {
      key: "actions",
      header: "",
      render: (c) => (
        <div className="flex items-center gap-space-1 justify-end">
          <button
            type="button"
            onClick={() => setEditTarget(c)}
            className="p-space-1 rounded-md text-gold-shadow hover:bg-white-warm"
            aria-label={`Edit ${c.code}`}
          >
            <Pencil size={16} aria-hidden />
          </button>
          {!c.isSystemReserved && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Delete category '${c.name}'?`)) {
                  deleteMutation.mutate(c.code);
                }
              }}
              disabled={deleteMutation.isPending}
              className="p-space-1 rounded-md text-gray-mid hover:text-status-error hover:bg-white-warm"
              aria-label={`Delete ${c.code}`}
            >
              <Trash2 size={16} aria-hidden />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Categories"
        description="Top-level folders for the knowledge base. Each category is a folder in the gated library; use the Access matrix to restrict it to specific positions."
        actions={
          <Button type="button" onClick={() => setEditTarget("new")}>
            <Plus size={16} aria-hidden /> New category
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={listQ.data ?? []}
        loading={listQ.isLoading}
        error={listQ.isError ? (listQ.error as Error).message : null}
        emptyMessage="No categories yet. Click 'New category' to add one."
        rowKey={(c) => c.code}
      />

      {editTarget !== null && (
        <CategoryEditDialog
          target={editTarget}
          existingCodes={(listQ.data ?? []).map((c) => c.code)}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            qc.invalidateQueries({ queryKey: QUERY_KEY });
          }}
        />
      )}
    </>
  );
}

interface CategoryEditDialogProps {
  target: KbCategory | "new";
  existingCodes: string[];
  onClose: () => void;
  onSaved: () => void;
}

const CATEGORY_CODE_REGEX = /^[A-Za-z0-9_-]{1,50}$/;

function CategoryEditDialog({
  target,
  existingCodes,
  onClose,
  onSaved,
}: CategoryEditDialogProps) {
  const isNew = target === "new";
  const initial = isNew ? undefined : target;
  const toast = useToast();
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [displayOrder, setDisplayOrder] = useState(
    initial?.displayOrder !== undefined ? String(initial.displayOrder) : "0",
  );
  const [errors, setErrors] = useState<string[]>([]);

  const createMutation = useMutation({
    mutationFn: () =>
      knowledgeBaseApi.createCategory({
        code: code.trim(),
        name: name.trim(),
        displayOrder: parseInt(displayOrder, 10) || 0,
      }),
    onSuccess: () => {
      toast.success("Category created.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      initial
        ? knowledgeBaseApi.updateCategory(initial.code, {
            name: name.trim(),
            displayOrder: parseInt(displayOrder, 10) || 0,
          })
        : Promise.reject(new Error("No category to update.")),
    onSuccess: () => {
      toast.success("Category updated.");
      onSaved();
    },
    onError: (err) => surface(err),
  });

  const surface = (err: unknown) => {
    if (err instanceof ApiError && err.errors.length) setErrors(err.errors);
    else setErrors([err instanceof Error ? err.message : "Save failed."]);
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (isNew) {
      const trimmed = code.trim();
      if (!trimmed) issues.push("Code is required.");
      else if (!CATEGORY_CODE_REGEX.test(trimmed))
        issues.push("Code must be 1–50 letters, digits, hyphen, or underscore.");
      else if (existingCodes.some((c) => c.toLowerCase() === trimmed.toLowerCase()))
        issues.push(`Code '${trimmed}' already exists.`);
    }
    if (!name.trim()) issues.push("Name is required.");
    return issues;
  };

  const submit = () => {
    setErrors([]);
    const issues = validate();
    if (issues.length) {
      setErrors(issues);
      return;
    }
    if (isNew) createMutation.mutate();
    else updateMutation.mutate();
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={isNew ? "New category" : `Edit ${initial?.code}`}
      description="Categories are the top-level folders the knowledge base groups documents by."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={pending}>
            {isNew ? "Create category" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="space-y-space-3">
        <FormField
          label="Code"
          required={isNew}
          htmlFor="kb-cat-code"
          helperText={
            isNew
              ? "Permanent identifier. Letters, digits, '-' or '_'. Examples: SPVTrust, ComplianceLegal."
              : "Codes are permanent; existing documents reference this code."
          }
        >
          {isNew ? (
            <Input
              id="kb-cat-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={50}
              autoFocus
              className="font-mono"
            />
          ) : (
            <div className="font-mono text-onyx font-semibold px-space-2 py-[6px] bg-white-warm border border-border-light rounded-md inline-block">
              {initial?.code}
            </div>
          )}
        </FormField>

        <FormField label="Name" required htmlFor="kb-cat-name">
          <Input
            id="kb-cat-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            autoFocus={!isNew}
          />
        </FormField>

        <FormField
          label="Display order"
          htmlFor="kb-cat-order"
          helperText="Lower numbers appear first."
        >
          <Input
            id="kb-cat-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className="max-w-[8rem] font-mono"
          />
        </FormField>

        {errors.length > 0 && (
          <ul
            className="list-disc pl-space-4 text-caption text-status-error"
            role="alert"
          >
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
